import {Async, AsyncError, AsyncPage, Empty, Loaded} from 'Core/models/Async';
import {UnvalidatedEmail} from 'Core/models/Email';
import {EmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import {createAction} from 'Shared/utilities/actionCreator';
import unionize, {ofType, UnionOf} from 'unionize';
import {
  IndependentAgentRegistration,
  UnvalidatedIndependentAgentRegistration,
  ValidIndependentAgentRegistration,
} from 'Users/models/IndependentAgent';
import {
  EmptyPreregisteredAgentFilter,
  PreregisteredAgent,
  PreregisteredAgentFilter,
} from 'Users/models/PreregisteredAgent';
import {PreregisteredUserPayload} from 'Users/models/PreregisteredUserPayload';
import {SecurityQuestionPayload} from 'Users/models/SecurityQuestionPayload';

export const LinkAgentState = unionize({
  linkingAgent: ofType<{}>(),
  linkingError: ofType<AsyncError>(),
});

export type LinkAgentUnion = UnionOf<typeof LinkAgentState>;

export enum IndependentAgentActionType {
  CLEAR = 'independentAgent/CLEAR',
  RESEND_EMAIL = 'independentAgent/RESEND_EMAIL',
  DELETE_PREREGISTRATION = 'independentAgent/DELETE_PREREGISTRATION ',
  CHANGE_AGENT = 'independentAgent/CHANGE_AGENT',
  FETCH_PREREGISTERED_AGENTS = 'independentAgent/FETCH_PREREGISTERED_AGENTS',
  FETCH_AGENT = 'independentAgent/FETCH_AGENT',
  LOAD_AGENT = 'independentAgent/LOAD_AGENT ',
  LINK_AGENCY = 'independentAgent/LINK_AGENCY ',
  CHANGE_LINK_AGENCY = 'independentAgent/CHANGE_LINK_AGENCY ',
  CHANGE_PREREGISTERED_AGENTS = 'independentAgent/CHANGE_PREREGISTERED_AGENTS',
  CHANGE_PREREGISTERED_AGENT_FILTER = 'independentAgent/CHANGE_PREREGISTERED_AGENT_FILTER',
  PREREGISTER_AGENT = 'independentAgent/PREREGISTER_AGENT',
  GET_SECURITY_QUESTIONS = 'independentAgent/GET_SECURITY_QUESTIONS',
  CHANGE_SECURITY_QUESTIONS = 'independentAgent/CHANGE_SECURITY_QUESTIONS',
  GET_PREREGISTERED_USER = 'independentAgent/GET_PREREGISTERED_USER',
  CHANGE_PREREGISTERED_USER = 'independentAgent/CHANGE_PREREGISTERED_USER',
}

export const clear = () => createAction(IndependentAgentActionType.CLEAR);
export const getPreregisteredUser = (preregisteredUserKey: string) =>
  createAction(IndependentAgentActionType.GET_PREREGISTERED_USER, {
    preregisteredUserKey,
  });
export const changePreregisteredUser = (preregisteredUser: Async<PreregisteredUserPayload>) =>
  createAction(IndependentAgentActionType.CHANGE_PREREGISTERED_USER, preregisteredUser);
export const getSecurityQuestions = () => createAction(IndependentAgentActionType.GET_SECURITY_QUESTIONS);
export const getPreregisteredAgents = (
  filter: PreregisteredAgentFilter,
  page?: Page<PreregisteredAgent>,
  encryptedIdentifier?: string
) => createAction(IndependentAgentActionType.FETCH_PREREGISTERED_AGENTS, {page, filter, encryptedIdentifier});

export const deletePreregistration = (userId: string, filter: PreregisteredAgentFilter, encryptedIdentifier?: string) =>
  createAction(IndependentAgentActionType.DELETE_PREREGISTRATION, {userId, encryptedIdentifier, filter});
export const resendEmail = (userId: string, encryptedIdentifier?: string) =>
  createAction(IndependentAgentActionType.RESEND_EMAIL, {userId, encryptedIdentifier});
export const changePreregisteredAgents = (preregisteredAgents: AsyncPage<Page<PreregisteredAgent>>) =>
  createAction(IndependentAgentActionType.CHANGE_PREREGISTERED_AGENTS, preregisteredAgents);
export const changePreregisteredAgentFilter = (preregisteredAgentFilter: PreregisteredAgentFilter) =>
  createAction(IndependentAgentActionType.CHANGE_PREREGISTERED_AGENT_FILTER, preregisteredAgentFilter);
export const changeSecurityQuestions = (securityQuestions: Async<SecurityQuestionPayload[]>) =>
  createAction(IndependentAgentActionType.CHANGE_SECURITY_QUESTIONS, securityQuestions);
export const changeLinkAgency = (linkAgencyState: LinkAgentUnion) =>
  createAction(IndependentAgentActionType.CHANGE_LINK_AGENCY, linkAgencyState);
export const linkAgency = (username: string, userKey: string) =>
  createAction(IndependentAgentActionType.LINK_AGENCY, {userKey, username});
export const fetchIndependentAgent = (userId: string) => createAction(IndependentAgentActionType.FETCH_AGENT, {userId});
export const loadIndependentAgent = (agent: Async<IndependentAgentRegistration>) =>
  createAction(IndependentAgentActionType.LOAD_AGENT, agent);
export const changeIndpendentAgent = (agent: Async<IndependentAgentRegistration>) =>
  createAction(IndependentAgentActionType.CHANGE_AGENT, agent);
export const preregisterAgent = (registration: ValidIndependentAgentRegistration, encryptedIdentifier?: string) =>
  createAction(IndependentAgentActionType.PREREGISTER_AGENT, {registration, encryptedIdentifier});

type IndependentAgentState = {
  independentAgent: Async<IndependentAgentRegistration>;
  linkAgencyState: LinkAgentUnion;
  preregisteredAgentsPage: AsyncPage<Page<PreregisteredAgent>>;
  preregisteredAgentFilter: PreregisteredAgentFilter;
  securityQuestions: Async<SecurityQuestionPayload[]>;
  preregisteredUser: Async<PreregisteredUserPayload>;
};

const DEFAULT_STATE: IndependentAgentState = {
  independentAgent: new Loaded(
    new UnvalidatedIndependentAgentRegistration(new EmptyString(), new EmptyString(), new UnvalidatedEmail(''), [], [])
  ),
  securityQuestions: new Empty(),
  preregisteredAgentsPage: new Empty(),
  linkAgencyState: LinkAgentState.linkingAgent(),
  preregisteredAgentFilter: EmptyPreregisteredAgentFilter,
  preregisteredUser: new Empty(),
};

type IndependentAgentAction =
  | ReturnType<typeof changePreregisteredUser>
  | ReturnType<typeof changeSecurityQuestions>
  | ReturnType<typeof changePreregisteredAgentFilter>
  | ReturnType<typeof changePreregisteredAgents>
  | ReturnType<typeof changeLinkAgency>
  | ReturnType<typeof clear>
  | ReturnType<typeof changeIndpendentAgent>
  | ReturnType<typeof loadIndependentAgent>;

export default (
  state: IndependentAgentState = DEFAULT_STATE,
  action: IndependentAgentAction
): IndependentAgentState => {
  switch (action.type) {
    case IndependentAgentActionType.CLEAR:
      return DEFAULT_STATE;
    case IndependentAgentActionType.CHANGE_PREREGISTERED_USER:
      return {
        ...state,
        preregisteredUser: action.payload,
      };
    case IndependentAgentActionType.CHANGE_SECURITY_QUESTIONS:
      return {
        ...state,
        securityQuestions: action.payload,
      };
    case IndependentAgentActionType.CHANGE_PREREGISTERED_AGENT_FILTER:
      return {
        ...state,
        preregisteredAgentFilter: action.payload,
      };
    case IndependentAgentActionType.CHANGE_PREREGISTERED_AGENTS:
      return {
        ...state,
        preregisteredAgentsPage: action.payload,
      };
    case IndependentAgentActionType.CHANGE_LINK_AGENCY:
      return {
        ...state,
        linkAgencyState: action.payload,
      };
    case IndependentAgentActionType.LOAD_AGENT:
      return {
        ...state,
        independentAgent: action.payload,
      };
    case IndependentAgentActionType.CHANGE_AGENT:
      return {...state, independentAgent: action.payload};
    default:
      return state;
  }
};
